/**
 * React Query mutation hook for deleting synced data that matches a
 * newly-added DNT filter entry (email address or domain). POSTs to:
 *   /api/orgs/{orgId}/integrations/{integrationEntityId}/delete-filtered-data/
 *
 * Returns a 202 with the created deletion jobs. On success, invalidates
 * the gmail-status and outlook-status queries so the integration card
 * refetches.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiPost } from '@/lib/http/client';

export interface DeleteFilteredDataInput {
  orgId: string;
  integrationEntityId: string;
  emails: string[];
  domains: string[];
}

export interface DeleteFilteredDataResponse {
  jobs: Array<{
    id: number;
    entity_type: string;
    status: string;
    items_processed: number;
    items_estimated: number | null;
    items_failed: number;
    progress: {
      items_processed: number;
      total_estimated: number | null;
      percent_complete: number | null;
      estimated_completion_at: string | null;
    };
    started_at: string | null;
    completed_at: string | null;
    last_progress_at: string | null;
    last_error: string | null;
  }>;
}

async function deleteFilteredSyncedData(
  params: DeleteFilteredDataInput
): Promise<DeleteFilteredDataResponse> {
  const url = `/api/orgs/${params.orgId}/integrations/${params.integrationEntityId}/delete-filtered-data/`;
  return apiPost<DeleteFilteredDataResponse>(url, {
    emails: params.emails,
    domains: params.domains,
  });
}

export function useDeleteFilteredSyncedData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFilteredSyncedData,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['google', 'gmail', 'status', variables.orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['microsoft', 'outlook', 'status', variables.orgId],
      });
    },
  });
}
